import type React from 'react';
import {useMemo, useState} from 'react';
import {
    Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField,
} from '@mui/material';
import {searchIndex} from '../domain/categorySearch';
import {errorText} from '../hooks/notices';
import type {ExploreNode} from '../types/categoryProfileTypes';
import type {SignalInput, SignalOptions} from '../types/signalsTypes';

type Props = {
    tree: ExploreNode[];
    options: SignalOptions;
    onSubmit: (input: SignalInput) => Promise<unknown>;
    onClose: () => void;
};

const today = () => new Date().toISOString().slice(0, 10);

/** Add an external signal by hand (staff, while the provider feed is off), routed to one category. */
export const SignalDialog: React.FC<Props> = ({tree, options, onSubmit, onClose}) => {
    const categories = useMemo(() => searchIndex(tree), [tree]);
    const [draft, setDraft] = useState<SignalInput>({
        path: '', publishedAt: today(), title: '', summary: '', url: '', publisher: '', language: 'en', impact: 'medium',
        impactType: options.impactTypes[0]?.value ?? 'price', eventCategory: '', sentiment: 'neutral', relevance: 60,
        exposure: '', rationale: '',
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const set = <K extends keyof SignalInput>(k: K, v: SignalInput[K]) => setDraft({...draft, [k]: v});
    const ready = draft.path && draft.title.trim() && draft.publishedAt;
    const submit = () => {
        setBusy(true);
        setError(null);
        onSubmit(draft).then(onClose).catch(e => setError(errorText(e))).finally(() => setBusy(false));
    };
    const select = (label: string, k: 'impact' | 'impactType' | 'sentiment', values: {value: string; label: string}[]) => (
        <TextField select size="small" label={label} value={draft[k]} sx={{flex: 1}}
                   onChange={e => set(k, e.target.value as SignalInput[typeof k])}>
            {values.map(v => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
        </TextField>
    );
    const cap = (v: string) => `${v[0].toUpperCase()}${v.slice(1)}`;

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="cp-signal-title">
            <DialogTitle id="cp-signal-title">Add external signal</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Autocomplete options={categories} autoHighlight size="small"
                                  getOptionLabel={o => (o.trail ? `${o.trail} › ${o.name}` : o.name)}
                                  value={categories.find(c => c.path === draft.path) ?? null}
                                  onChange={(_, v) => set('path', v?.path ?? '')}
                                  renderInput={params => <TextField {...params} label="Category" helperText="The signal shows on this category and its parents"/>}/>
                    <TextField label="Title" size="small" value={draft.title} onChange={e => set('title', e.target.value)}
                               slotProps={{htmlInput: {maxLength: 300}}}/>
                    <TextField label="Summary" size="small" multiline minRows={2} value={draft.summary}
                               onChange={e => set('summary', e.target.value)} slotProps={{htmlInput: {maxLength: 5000}}}/>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField label="Publisher" size="small" value={draft.publisher} sx={{flex: 1}}
                                   onChange={e => set('publisher', e.target.value)} slotProps={{htmlInput: {maxLength: 120}}}/>
                        <TextField label="Link" size="small" value={draft.url} sx={{flex: 2}} placeholder="https://"
                                   onChange={e => set('url', e.target.value)} slotProps={{htmlInput: {maxLength: 600}}}/>
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField label="Published" size="small" type="date" value={draft.publishedAt} sx={{flex: 1}}
                                   onChange={e => set('publishedAt', e.target.value)} slotProps={{inputLabel: {shrink: true}}}/>
                        <TextField label="Language" size="small" value={draft.language} sx={{flex: 1}}
                                   onChange={e => set('language', e.target.value.toLowerCase())} slotProps={{htmlInput: {maxLength: 8}}}/>
                        <TextField label="Event" size="small" value={draft.eventCategory} sx={{flex: 1}} placeholder="Strike, tariff…"
                                   onChange={e => set('eventCategory', e.target.value)} slotProps={{htmlInput: {maxLength: 60}}}/>
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        {select('Impact', 'impact', options.impacts.map(v => ({value: v, label: cap(v)})))}
                        {select('Impact type', 'impactType', options.impactTypes)}
                        {select('Sentiment', 'sentiment', options.sentiments.map(v => ({value: v, label: cap(v)})))}
                        <TextField label="Relevance" size="small" type="number" value={draft.relevance} sx={{flex: 1}}
                                   onChange={e => set('relevance', Math.max(0, Math.min(100, Number(e.target.value))))}
                                   slotProps={{htmlInput: {min: 0, max: 100}}}/>
                    </Stack>
                    <TextField label="Exposure" size="small" value={draft.exposure} onChange={e => set('exposure', e.target.value)}
                               helperText="What in the category is exposed, for a routed alert" slotProps={{htmlInput: {maxLength: 2000}}}/>
                    <TextField label="Why it matters" size="small" multiline minRows={2} value={draft.rationale}
                               onChange={e => set('rationale', e.target.value)} slotProps={{htmlInput: {maxLength: 2000}}}/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !ready}>Add signal</Button>
            </DialogActions>
        </Dialog>
    );
};
